import { createContext, useCallback, useContext, useState, type FC, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Modal } from '@carbon/react';

import { AVISO_DO_TITULAR, AVISO_SO_VER } from '@maestra/core/constants/bloqueios';
import { useArtistCapabilities } from '@maestra/core/hooks/useArtistCapabilities';
import { useAppSelector } from '@maestra/core/store/store';
import { ROTULOS } from '../padroes/rotulos';

// O AVISO DE QUEM NÃO PODE SALVAR PORQUE O TITULAR DO PERFIL NÃO ASSINA.
//
// Não é o upsell: aqui não há preço nem /planos, porque o gate de um perfil compartilhado é a
// assinatura do TITULAR, e o convidado que assinasse continuaria bloqueado. O aviso mora num
// contexto porque as tentativas de salvar estão espalhadas por várias telas: cada uma só chama
// `avisar()` onde antes havia um `return` mudo.
//
// Desde o acesso por módulo (03/10/2026) há dois motivos, e o aviso diz o certo: o titular sem
// assinatura (todo o perfil em leitura) ou o módulo liberado só para Ver.

const Contexto = createContext<() => void>(() => {});

/** Abre o aviso. Fora do provider é inócuo. */
export const useAvisoDoTitular = () => useContext(Contexto);

export const AvisoDoTitularProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [aberto, setAberto] = useState(false);
  const avisar = useCallback(() => setAberto(true), []);
  const fechar = () => setAberto(false);
  // O perfil aberto, pelo endereço: a casca está acima das rotas e não recebe o `:id`.
  const { pathname } = useLocation();
  const id = /^\/artists\/([^/]+)/.exec(pathname)?.[1];
  const artist = useAppSelector((s) => (id ? s.artists.items.find((a) => a.id === id) : undefined));
  const { somenteLeituraPeloTitular } = useArtistCapabilities(artist);
  const aviso = somenteLeituraPeloTitular ? AVISO_DO_TITULAR : AVISO_SO_VER;

  return (
    <Contexto.Provider value={avisar}>
      {children}
      {aberto && (
        <Modal
          open
          size='sm'
          modalLabel='Perfil'
          modalHeading={aviso.titulo}
          primaryButtonText={aviso.fechar}
          onRequestSubmit={fechar}
          onRequestClose={fechar}
          closeButtonLabel={ROTULOS.fechar}
        >
          <p>{aviso.corpo}</p>
          <p>{aviso.apoio}</p>
        </Modal>
      )}
    </Contexto.Provider>
  );
};
